'use client';

import { useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';

/* ==================================================================
   Six phases (Design + Development combined into Phase 3)
   ================================================================== */

const phases = [
  { badge: 'Phase 1', title: 'Discovery & Business Alignment' },
  { badge: 'Phase 2', title: 'Planning, Architecture & Proposal' },
  { badge: 'Phase 3', title: 'Design & Development' },
  { badge: 'Phase 4', title: 'QA & Acceptance' },
  { badge: 'Phase 5', title: 'Deployment & Go-Live' },
  { badge: 'Phase 6', title: 'Support, Optimization & Growth' },
];

const N = phases.length; // 6

// Runway: heading reveal + marker sweep + stage reveals (first ~40%), then a
// hold during which the Testimonials section slides over via parallax (see
// Testimonials.jsx). The marker must reach the line's end BEFORE the overlap.
const RUNWAY_VH = 400;

/* ==================================================================
   Section
   ================================================================== */

export default function ProductJourney() {
  const sectionRef = useRef(null);
  const headRef = useRef(null);
  const markerRef = useRef(null);
  const cardsRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    let raf;
    const clamp = (v, lo = 0, hi = 1) => Math.min(Math.max(v, lo), hi);

    const update = () => {
      const r = section.getBoundingClientRect();
      const runway = section.offsetHeight - window.innerHeight;
      const p = clamp(runway > 0 ? -r.top / runway : 0);
      section.style.setProperty('--journey-progress', p.toFixed(4));

      // Track marker reaches the right end at p=0.4, then holds. The stage
      // cards reveal from --journey-progress (see globals.css). Only after the
      // marker is parked does the Testimonials section slide over (its negative
      // margin is tuned so it enters the viewport around p=0.5).
      const trackP = clamp(p / 0.4);
      section.style.setProperty('--track-p', trackP.toFixed(4));
    };

    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    // Replay headline word-reveal each time the section head re-enters
    const head = headRef.current;
    let io;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (head && !reduce && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(
        entries => {
          entries.forEach(e => {
            if (e.isIntersecting) {
              head.classList.remove('in');
              // eslint-disable-next-line no-unused-expressions
              void head.offsetWidth;
              head.classList.add('in');
            } else {
              head.classList.remove('in');
            }
          });
        },
        { threshold: 0.2 }
      );
      io.observe(head);
    }

    // Phones (<=880px, see hero-home.css): cards stagger in automatically
    // when the grid scrolls into view, and reset once it is fully out of view.
    const cards = cardsRef.current;
    let cardsIo;
    if (cards && 'IntersectionObserver' in window) {
      cardsIo = new IntersectionObserver(
        entries => {
          entries.forEach(e => {
            if (e.isIntersecting) cards.classList.add('in');
            else cards.classList.remove('in');
          });
        },
        { threshold: 0.15 }
      );
      cardsIo.observe(cards);
    } else if (cards) {
      cards.classList.add('in');
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
      if (io) io.disconnect();
      if (cardsIo) cardsIo.disconnect();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      data-nav-theme="light"
      className="journey relative bg-[#f6f8fb]"
      style={{
        minHeight: `${RUNWAY_VH}vh`,
        isolation: 'isolate',
        ['--journey-progress']: 0,
        ['--track-p']: 0,
      }}
    >
      {/* Sticky pin */}
      <div className="journey-pin sticky top-0 flex h-screen flex-col overflow-hidden px-6 py-16 sm:px-10 lg:py-24">
        {/* Soft brand glow inside the pin */}
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[1000px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#058BF0]/20 blur-[180px]"
        />

        <div className="relative mx-auto flex w-11/12 max-w-[1280px] flex-1 flex-col gap-10 lg:gap-14">
          {/* Heading */}
          <div ref={headRef} className="journey-head">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#058BF0] sm:text-sm">
              Our Process
            </p>
            <h2 className="journey-title mt-5 max-w-[22ch] font-poppins text-3xl font-bold leading-[1.2] sm:leading-[1.2] lg:leading-[1.2] tracking-tight text-[#0a1b3a] sm:text-4xl lg:text-5xl xl:text-[3.5rem]">
              <span className="word">Our</span>{' '}
              <span className="word">Delivery</span>{' '}
              <span className="word">Journey</span>
            </h2>
          </div>

          {/* Horizontal track */}
          <div
            className="journey-track relative mx-auto h-[52px] w-full"
            aria-hidden
          >
            <div
              className="journey-track-line absolute inset-x-0 top-1/2 h-px -translate-y-1/2"
              style={{
                background:
                  'linear-gradient(to right, transparent, rgba(10,27,58,0.22), rgba(10,27,58,0.22), transparent)',
              }}
            />
            <div
              className="journey-track-fill absolute left-0 top-1/2 h-[2px]"
              style={{
                width: 'calc(var(--track-p, 0) * 100%)',
                background:
                  'linear-gradient(to right, #058BF0, #19a7ff, #5BB8FF)',
                transform: 'translateY(-1px)',
                boxShadow: '0 0 12px rgba(5, 139, 240, 0.55)',
              }}
            />
            <div
              className="journey-tooltip pointer-events-none absolute right-0 bottom-[calc(100%+12px)] whitespace-nowrap rounded-md bg-[#0a1b3a] px-3.5 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-white shadow-[0_8px_20px_-10px_rgba(10,27,58,0.5)]"
              style={{
                opacity:
                  'clamp(0, calc((var(--track-p, 0) - 0.92) * 12.5), 1)',
                transform:
                  'translateY(calc((1 - clamp(0, calc((var(--track-p, 0) - 0.92) * 12.5), 1)) * 8px))',
              }}
            >
              Live &amp; Growing
              <span
                aria-hidden
                className="absolute -bottom-[4px] right-3.5 h-2 w-2 rotate-45 bg-[#0a1b3a]"
              />
            </div>
            <div
              ref={markerRef}
              className="journey-marker absolute top-1/2 z-[2] grid h-11 w-11 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-lg border border-[#058BF0]/25 bg-white shadow-[0_6px_18px_rgba(5,139,240,0.3)]"
              style={{ left: 'calc(var(--track-p, 0) * 100%)' }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/new-logos/logo-mark.png"
                alt="Cloudastra"
                loading="lazy"
                decoding="async"
                className="h-6 w-auto"
              />
            </div>
          </div>

          {/* Cards row */}
          <div
            ref={cardsRef}
            className="journey-cards grid gap-3 lg:gap-5"
            style={{
              gridTemplateColumns: `repeat(${N}, minmax(0, 1fr))`,
              gridTemplateRows: 'auto 1fr',
            }}
          >
            {phases.map((p, i) => (
              <article
                key={i}
                className="journey-card relative grid rounded-2xl border border-gray-200/80 bg-white p-6 shadow-[0_1px_2px_rgba(10,27,58,0.04)] lg:p-7"
                style={{
                  ['--i']: i,
                  gridRow: 'span 2',
                  gridTemplateRows: 'subgrid',
                  gap: '18px',
                }}
              >
                <span className="journey-badge justify-self-start rounded-full border border-[#058BF0]/25 bg-[#058BF0]/10 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[#058BF0]">
                  {p.badge}
                </span>
                <h3 className="journey-card-title font-poppins text-[clamp(15px,1.4vw,19px)] font-bold leading-tight tracking-tight text-[#0a1b3a]">
                  {p.title}
                </h3>
              </article>
            ))}
          </div>

          {/* CTA at the bottom */}
          <div className="flex justify-center">
            <a
              href="/contact"
              className="group inline-flex items-center gap-2 rounded-lg bg-[#058BF0] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-cloud/30 transition hover:bg-[#0477cc]"
            >
              Discuss your product
              <ArrowRight
                className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                strokeWidth={2.2}
              />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
